import React from "react";
import TrackerAttachmentUploader from "./TrackerAttachmentUploader";
import {
  TRACKER_CALL_TYPE_OPTIONS,
  TRACKER_COVERAGE_OPTIONS,
  TRACKER_SOURCE_OPTIONS,
  TRACKER_VALID_ID_OPTIONS,
} from "../../utils/trackerBridge";

const INPUT_CLASS =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-blue-500 focus:outline-none";

const TrackerQueueEditModal = ({
  isOpen,
  loading,
  draft,
  ticketMeta,
  setDraft,
  attachments,
  onUpload,
  onRefresh,
  onDelete,
  onSave,
  onCloseTicket,
  onCancel,
  isSaving,
  mode = "edit",
}) => {
  if (!isOpen) {
    return null;
  }

  const isViewMode = mode === "view";
  const modalTitle = isViewMode ? "View Ticket Details" : "Edit Queued Ticket";
  const status = String(ticketMeta?.status || "").toLowerCase();
  const createdByName = ticketMeta?.created_by_name || "-";
  const takenOverByName = ticketMeta?.taken_over_by_name || "-";
  const currentHandlerName = ticketMeta?.owner_name || "-";

  const resolvedLastTouchedBy =
    takenOverByName && takenOverByName !== "-"
      ? takenOverByName
      : currentHandlerName && currentHandlerName !== "-"
      ? currentHandlerName
      : createdByName;

  let bannerText = `Last touched by: ${resolvedLastTouchedBy || "-"}.`;
  if (status === "open") {
    bannerText = `This ticket is currently handled by: ${currentHandlerName || "-"}.`;
  } else if (status === "closed") {
    bannerText = `Last touched by: ${resolvedLastTouchedBy || "-"}. Ticket is currently closed.`;
  } else if (status === "queued") {
    bannerText = `Last touched by: ${resolvedLastTouchedBy || "-"}. Ticket is currently queued.`;
  }

  const updateField = (field, value) => {
    if (isViewMode) return;

    setDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40 px-4">
      <div className="max-h-[95vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-6 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">{modalTitle}</h3>
              <p className="text-sm text-slate-600">
                Ticket <span className="font-semibold text-slate-900">#{draft?.tkt_num || "-"}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              Close
            </button>
          </div>
        </div>

        <div className="space-y-4 px-6 py-5">
          {loading ? (
            <p className="text-sm text-slate-600">Loading ticket details...</p>
          ) : (
            <>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-semibold text-amber-900">{bannerText}</p>
                <div className="mt-2 grid gap-2 text-xs text-amber-800 md:grid-cols-4">
                  <p>
                    Created By: <span className="font-semibold">{createdByName}</span>
                  </p>
                  <p>
                    Take Over By: <span className="font-semibold">{takenOverByName}</span>
                  </p>
                  <p>
                    Current Handler: <span className="font-semibold">{currentHandlerName}</span>
                  </p>
                  <p>
                    Status:{" "}
                    <span className="font-semibold capitalize">{ticketMeta?.status || draft?.status || "-"}</span>
                  </p>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Source
                  <select
                    value={draft.source || ""}
                    onChange={(event) => updateField("source", event.target.value)}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                  >
                    <option value="">Select source</option>
                    {TRACKER_SOURCE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Call Type
                  <select
                    value={draft.call_type || ""}
                    onChange={(event) => updateField("call_type", event.target.value)}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                  >
                    <option value="">Select call type</option>
                    {TRACKER_CALL_TYPE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Coverage
                  <select
                    value={draft.coverage || ""}
                    onChange={(event) => updateField("coverage", event.target.value)}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                  >
                    <option value="">Select coverage</option>
                    {TRACKER_COVERAGE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Time Hour
                  <input
                    value={draft.time_received_hour || ""}
                    onChange={(event) => updateField("time_received_hour", event.target.value)}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                    placeholder="Hour"
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Time Minute
                  <input
                    value={draft.time_received_minute || ""}
                    onChange={(event) => updateField("time_received_minute", event.target.value)}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                    placeholder="Minute"
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  AM / PM
                  <select
                    value={draft.time_received_ampm || ""}
                    onChange={(event) => updateField("time_received_ampm", event.target.value)}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                  >
                    <option value="">Select</option>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </label>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Member
                  <input value={draft.member_name || ""} readOnly className={`${INPUT_CLASS} bg-slate-100`} />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Provider
                  <input value={draft.provider_name || ""} readOnly className={`${INPUT_CLASS} bg-slate-100`} />
                </label>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Valid ID
                  <select
                    value={draft.valid_id || ""}
                    onChange={(event) => updateField("valid_id", event.target.value)}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                  >
                    <option value="">Select valid ID</option>
                    {TRACKER_VALID_ID_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  ID Number
                  <input
                    value={draft.id_num || ""}
                    onChange={(event) => updateField("id_num", event.target.value)}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                  />
                </label>

                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Doctor
                  <input
                    value={draft.doctor || ""}
                    onChange={(event) => updateField("doctor", event.target.value)}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                  />
                </label>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Given Diagnosis
                  <textarea
                    value={draft.diagnosis || ""}
                    onChange={(event) => updateField("diagnosis", event.target.value)}
                    rows={2}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Lab Procedures
                  <textarea
                    value={draft.procedure || ""}
                    onChange={(event) => updateField("procedure", event.target.value)}
                    rows={2}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                  />
                </label>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  TRN#
                  <input
                    value={draft.trans_no || ""}
                    onChange={(event) => updateField("trans_no", event.target.value)}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                  />
                </label>

                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  LOA#
                  <input
                    value={draft.loa_no || ""}
                    onChange={(event) => updateField("loa_no", event.target.value)}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                  />
                </label>

                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Form#
                  <input
                    value={draft.form_number || ""}
                    onChange={(event) => updateField("form_number", event.target.value)}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                  />
                </label>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Disapproved Amt
                  <input
                    type="number"
                    value={draft.disapproved_amt || ""}
                    onChange={(event) => updateField("disapproved_amt", event.target.value)}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Disapproved Remarks
                  <textarea
                    value={draft.disapproved_remarks || ""}
                    onChange={(event) => updateField("disapproved_remarks", event.target.value)}
                    rows={2}
                    disabled={isViewMode || isSaving}
                    className={INPUT_CLASS}
                  />
                </label>
              </div>

              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Remarks
                <textarea
                  value={draft.remarks || ""}
                  onChange={(event) => updateField("remarks", event.target.value)}
                  rows={2}
                  disabled={isViewMode || isSaving}
                  className={INPUT_CLASS}
                />
              </label>

              <TrackerAttachmentUploader
                source={draft.source}
                ticketNumber={draft.tkt_num}
                attachments={attachments}
                uploading={isSaving}
                onUpload={onUpload}
                onRefresh={onRefresh}
                onDelete={onDelete}
                readOnly={isViewMode}
              />
            </>
          )}
        </div>

        <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-white px-6 py-4">
          {isViewMode ? (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              Close
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onCancel}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSaving}
                onClick={onSave}
                className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-70"
              >
                {isSaving ? "Saving..." : "Save Queue Edit"}
              </button>
              <button
                type="button"
                disabled={isSaving}
                onClick={onCloseTicket}
                className="rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-70"
              >
                {isSaving ? "Please wait..." : "Close Ticket"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrackerQueueEditModal;
